import { useState } from 'react';
import { X, Plus, ChevronDown } from 'lucide-react';
import { useT } from '../i18n';
import { BareTextarea, newDeliverable, type Deliverable } from './ProjectForm';

// Editable deliverable cards (title + expandable body) — replaces the old
// flat BulletList editor so deliverables can carry a longer description.
export function DeliverablesEditor({ deliverables, onChange, canEdit }: {
  deliverables: Deliverable[];
  onChange: (d: Deliverable[]) => void;
  canEdit: boolean;
}) {
  const { t } = useT();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) => setExpanded(s => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const update = (id: string, patch: Partial<Deliverable>) =>
    onChange(deliverables.map(d => d.id === id ? { ...d, ...patch } : d));

  const remove = (id: string) => onChange(deliverables.filter(d => d.id !== id));

  const add = () => {
    const d = newDeliverable();
    onChange([...deliverables, d]);
    setExpanded(s => new Set(s).add(d.id));
  };

  return (
    <div>
      {deliverables.length === 0 && (
        <div className="text-sm text-ink-3" style={{ fontStyle: 'italic', marginBottom: 'var(--s8)' }}>{t('deliverablesEditor.empty')}</div>
      )}
      <div className="flex flex-col gap-8">
        {deliverables.map(d => {
          const isOpen = expanded.has(d.id);
          return (
            <div key={d.id} className="deliv-card">
              <div className="deliv-card-head">
                <button type="button" className="deliv-fold" onClick={() => toggle(d.id)} title={isOpen ? t('deliverablesEditor.collapse') : t('deliverablesEditor.expand')}>
                  <ChevronDown size={14} style={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
                </button>
                {canEdit ? (
                  <input
                    className="form-input--bare"
                    style={{ fontWeight: 600 }}
                    value={d.title}
                    placeholder={t('deliverablesEditor.titlePlaceholder')}
                    onChange={(e) => update(d.id, { title: e.target.value })}
                  />
                ) : (
                  <span style={{ fontWeight: 600, cursor: 'pointer' }} onClick={() => toggle(d.id)}>{d.title || '—'}</span>
                )}
                {canEdit && (
                  <button type="button" className="tag-remove" title={t('common.remove')} onClick={() => remove(d.id)}>
                    <X className="icon-sm" style={{ width: 12, height: 12 }} />
                  </button>
                )}
              </div>
              {isOpen && (
                canEdit ? (
                  <BareTextarea value={d.body} placeholder={t('deliverablesEditor.bodyPlaceholder')} onChange={(v) => update(d.id, { body: v })} />
                ) : (
                  <div className="form-input--bare" style={{ cursor: 'default', whiteSpace: 'pre-wrap' }}>{d.body || '—'}</div>
                )
              )}
            </div>
          );
        })}
      </div>
      {canEdit && (
        <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 'var(--s12)' }} onClick={add}>
          <Plus className="icon-sm" /> {t('deliverablesEditor.add')}
        </button>
      )}
    </div>
  );
}

// Read-only accordion list — title always shown, body folds out on click.
// Used wherever deliverables are just displayed (Planning tab, info popovers).
export function DeliverableAccordionList({ deliverables }: { deliverables: Deliverable[] }) {
  const { t } = useT();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setExpanded(s => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  if (deliverables.length === 0) {
    return <div className="text-sm text-ink-3" style={{ fontStyle: 'italic' }}>{t('deliverablesEditor.noneDefined')}</div>;
  }

  return (
    <div className="flex flex-col gap-8">
      {deliverables.map(d => {
        const isOpen = expanded.has(d.id);
        return (
          <div key={d.id} className="deliv-card">
            <button
              type="button"
              className="deliv-card-head"
              style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() => toggle(d.id)}
              disabled={!d.body}
            >
              <ChevronDown size={14} style={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)', opacity: d.body ? 1 : 0.3 }} />
              <span style={{ fontWeight: 600 }}>{d.title || t('deliverablesEditor.untitled')}</span>
            </button>
            {isOpen && d.body && (
              <div className="form-input--bare" style={{ cursor: 'default', whiteSpace: 'pre-wrap' }}>{d.body}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
